import {
  ActionIcon,
  Box,
  Divider,
  FileButton,
  Group,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconArrowUp, IconPaperclip, IconPlayerStopFilled } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { chatFileAcceptHint, chatFileButtonAccept, ChatFileTypes } from '../chats/chatFileTypes';
import { useConversations } from '../chats/useConversations';
import { useHistoryScope } from '../chats/useHistoryScope';
import { ChatFile, UploadingChatFile } from '../chats/types';
import { SiestaMode } from '../types';
import { CHAT_BAR_TAB_LABELS } from './ChatModeTabs';
import { ChatFileList } from './ChatFileList';

type Props = {
  conversationId: string;
  siestaMode: SiestaMode;
  pendingFiles: ChatFile[];
  uploadingFiles: UploadingChatFile[];
  fileTypes?: ChatFileTypes;
  onSubmit: (message: string) => void;
  onFocus?: () => void;
  isProcessing?: boolean;
  onStop?: () => void;
  onAttachFiles: (files: File[]) => void;
  onRemovePendingFile: (file: ChatFile) => void;
  isUploading: boolean;
  dense?: boolean;
};

export function SiestaChatInput({
  conversationId,
  siestaMode,
  pendingFiles,
  uploadingFiles,
  fileTypes,
  onSubmit,
  onFocus,
  isProcessing,
  onStop,
  onAttachFiles,
  onRemovePendingFile,
  isUploading,
  dense,
}: Props) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { clientId, familyId } = useHistoryScope();
  const { data: conversations = [] } = useConversations(clientId, familyId);
  const chatName =
    conversations.find(conversation => conversation.id === conversationId)?.title ?? null;
  const canAttach = siestaMode === 'chat';
  let attachTooltip = '';
  if (!canAttach) {
    attachTooltip = `Files can only be attached in the ${CHAT_BAR_TAB_LABELS.chat} tab`;
  } else if (fileTypes) {
    attachTooltip = chatFileAcceptHint(fileTypes);
  }

  // Put the cursor in the box on entering a chat, opening a conversation, or picking an action
  // (all of which land in chat mode), so the user can type without clicking in first.
  useEffect(() => {
    if (siestaMode === 'chat') {
      inputRef.current?.focus();
    }
  }, [siestaMode, conversationId]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!isProcessing && !isUploading && trimmed) {
      onSubmit(trimmed);
      setValue('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box
      c="white"
      px="xl"
      pt="md"
      pb={dense ? 'md' : 'xxxl'}
      style={{
        borderTop: '1px solid rgba(255, 255, 255, 0.12)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
      }}
    >
      {canAttach && (
        <ChatFileList
          files={pendingFiles}
          uploadingFiles={uploadingFiles}
          onRemove={onRemovePendingFile}
        />
      )}

      <Textarea
        ref={inputRef}
        variant="unstyled"
        size="md"
        placeholder="Ask me anything"
        value={value}
        onChange={event => setValue(event.currentTarget.value)}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        autosize
        minRows={1}
        maxRows={dense ? 4 : 6}
        styles={{
          input: {
            border: 'none',
            paddingLeft: 0,
            paddingBottom: 0,
            color: 'var(--mantine-color-white)',
            '--input-placeholder-color': 'var(--mantine-color-white)',
          },
        }}
      />

      <Divider mt={2} mb="sm" color="gray.4" />

      <Group justify="space-between" align="center" wrap="nowrap">
        <Tooltip label={attachTooltip} disabled={!attachTooltip} position="top" withArrow>
          <Box style={{ display: 'inline-flex' }}>
            <FileButton
              multiple
              onChange={onAttachFiles}
              accept={fileTypes ? chatFileButtonAccept(fileTypes.extensions) : undefined}
            >
              {props => (
                <ActionIcon
                  {...props}
                  variant="filled"
                  color="gray"
                  size="lg"
                  aria-label="Attach file"
                  loading={isUploading}
                  disabled={!canAttach}
                  style={{ borderRadius: '50%' }}
                >
                  <IconPaperclip size={18} />
                </ActionIcon>
              )}
            </FileButton>
          </Box>
        </Tooltip>

        {chatName && (
          <Text flex={1} ta="center" size="sm" fw={FONT_WEIGHTS.medium} lineClamp={1}>
            {`Chat: ${chatName}`}
          </Text>
        )}

        {isProcessing ? (
          <ActionIcon
            variant="filled"
            color="gray"
            size="lg"
            aria-label="Stop generating"
            onClick={onStop}
            style={{ borderRadius: '50%' }}
          >
            <IconPlayerStopFilled size={18} />
          </ActionIcon>
        ) : (
          <ActionIcon
            variant="filled"
            color="gray"
            size="lg"
            aria-label="Send message"
            onClick={handleSubmit}
            disabled={!value.trim() || isUploading}
            style={{ borderRadius: '50%' }}
          >
            <IconArrowUp size={18} />
          </ActionIcon>
        )}
      </Group>
    </Box>
  );
}
